import { memo } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, MessageCircle, Phone } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/translations";
import { LocalizedLink } from "@/components/LocalizedLink";
import { QuickCallbackForm } from "@/components/QuickCallbackForm";
import { UrgencyBadge } from "@/components/UrgencyBadge";

// Memoized Hero component to prevent unnecessary re-renders
export const Hero = memo(() => {
  const { language } = useLanguage();
  const t = translations[language];

  const heroTexts = {
    tr: {
      priceFrom: "€1.500'den başlayan fiyatlar",
      whatsappCta: "WhatsApp ile Bilgi Al",
      callbackTitle: "30 dakika içinde geri arama",
    },
    en: {
      priceFrom: "Starting from €1,500",
      whatsappCta: "Get Info via WhatsApp",
      callbackTitle: "Callback within 30 minutes",
    },
    fr: {
      priceFrom: "À partir de €1.500",
      whatsappCta: "Info via WhatsApp",
      callbackTitle: "Rappel dans 30 minutes",
    },
    nl: {
      priceFrom: "Vanaf €1.500",
      whatsappCta: "Info via WhatsApp",
      callbackTitle: "Terugbellen binnen 30 min",
    },
    ar: {
      priceFrom: "بدءاً من €1,500",
      whatsappCta: "معلومات عبر واتساب",
      callbackTitle: "رد خلال 30 دقيقة",
    },
  };

  const ht = heroTexts[language as keyof typeof heroTexts] || heroTexts.en;

  const whatsappMessages: Record<string, string> = {
    en: "Hello! I would like to get information about hair transplant.",
    nl: "Hallo! Ik wil graag informatie over een haartransplantatie.",
    tr: "Merhaba! Saç ekimi hakkında bilgi almak istiyorum.",
    fr: "Bonjour! Je voudrais des informations sur la greffe de cheveux.",
    ar: "مرحباً! أريد الحصول على معلومات حول زراعة الشعر.",
  };
  const waMessage = whatsappMessages[language] || whatsappMessages.en;
  const waHref = `https://wa.me/31638523131?text=${encodeURIComponent(waMessage)}`;
  
  return (
    <section className="relative min-h-screen min-h-[100dvh] flex items-center pt-28 md:pt-[7.5rem]">
      {/* Optimized hero background - native img for best LCP with mobile optimizations */}
      <div 
        className="absolute inset-0 z-0 gpu-accelerated"
        aria-hidden="true"
      >
        <picture>
          <source
            type="image/avif"
            srcSet="/hero/hero-640.avif 640w, /hero/hero-1024.avif 1024w, /hero/hero-1600.avif 1600w"
            sizes="100vw"
          />
          <source
            type="image/webp"
            srcSet="/hero/hero-640.webp 640w, /hero/hero-1024.webp 1024w, /hero/hero-1600.webp 1600w"
            sizes="100vw"
          />
          <img
            src="/hero/hero-1024.jpg"
            srcSet="/hero/hero-640.jpg 640w, /hero/hero-1024.jpg 1024w, /hero/hero-1600.jpg 1600w"
            sizes="100vw"
            alt=""
            width={1600}
            height={900}
            fetchPriority="high"
            decoding="async"
            loading="eager"
            className="absolute inset-0 w-full h-full object-cover will-change-auto"
            style={{ filter: 'brightness(0.5)' }}
          />
        </picture>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl">
          {/* Urgency Badge */}
          <div className="mb-4">
            <UrgencyBadge variant="slots" />
          </div>

          {/* Price Badge */}
          <div className="inline-flex items-center gap-2 bg-green-500/90 text-white px-4 py-2 rounded-full text-sm font-bold mb-4 backdrop-blur-sm">
            <span>💰</span>
            <span>{ht.priceFrom}</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 md:mb-6 leading-tight">
            {t.hero.title}
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 mb-6 md:mb-8">
            {t.hero.subtitle}
          </p>

          {/* Primary CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
            <LocalizedLink to="/book-consultation">
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary/90 text-primary-foreground text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 shadow-lg hover:shadow-xl transition-all min-h-[48px] touch-manipulation w-full sm:w-auto"
              >
                {t.hero.bookConsultation}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </LocalizedLink>
            
            {/* WhatsApp CTA - Primary Action */}
            <Button 
              asChild
              size="lg" 
              className="bg-[#25D366] hover:bg-[#20BA5A] text-white text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 shadow-lg hover:shadow-xl transition-all min-h-[48px] touch-manipulation w-full sm:w-auto"
            >
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <MessageCircle className="h-5 w-5" />
                {ht.whatsappCta}
              </a>
            </Button>
          </div>

          {/* Quick Callback Form */}
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 sm:p-6 border border-white/20 mb-8">
            <div className="flex items-center gap-2 text-white/90 text-sm mb-3">
              <Phone className="w-4 h-4" />
              <span className="font-medium">{ht.callbackTitle}</span>
            </div>
            <QuickCallbackForm variant="hero" />
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-2xl">
            <div className="text-center">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-1 md:mb-2">★★★★★</div>
              <div className="text-white/80 text-xs sm:text-sm md:text-base">Patient Reviews</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

Hero.displayName = 'Hero';
